import { forwardRef, InputHTMLAttributes, useEffect, useMemo, useRef, useState } from 'react'

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'maxLength' | 'pattern'>

// A 6-box OTP style input. It renders six visible inputs for UX,
// and a hidden input bound to RHF via ref/name to submit the combined value.
export const CodeInput6 = forwardRef<HTMLInputElement, Props>(function CodeInput6(
  { className = '', onChange, name, defaultValue, value, ...props },
  ref
) {
  const [digits, setDigits] = useState<string[]>(() => {
    const initial = typeof value === 'string' && value.length === 6
      ? value
      : typeof defaultValue === 'string'
        ? defaultValue
        : ''
    return Array.from({ length: 6 }, (_, i) => initial[i] ?? '')
  })
  const inputsRef = useRef<Array<HTMLInputElement | null>>([])
  const hiddenRef = useRef<HTMLInputElement | null>(null)

  // Expose the hidden input to the forwarded ref (RHF needs it)
  useEffect(() => {
    if (!ref) return
    if (typeof ref === 'function') ref(hiddenRef.current as any)
    else (ref as any).current = hiddenRef.current
  }, [ref])

  const combined = useMemo(() => digits.join(''), [digits])

  // Sync external controlled value into boxes if provided
  useEffect(() => {
    if (typeof value === 'string' && value !== combined) {
      const norm = (value || '').slice(0, 6)
      setDigits(Array.from({ length: 6 }, (_, i) => norm[i] ?? ''))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const notifyChange = (next: string) => {
    if (hiddenRef.current) {
      hiddenRef.current.value = next
      const ev = new Event('input', { bubbles: true })
      hiddenRef.current.dispatchEvent(ev)
    }
    onChange?.({ target: { value: next, name } } as any)
  }

  const handleBoxChange = (idx: number, raw: string) => {
    let v = raw.replace(/\D+/g, '')
    // If user pasted the full code into any box
    if (v.length === 6) {
      const arr = Array.from({ length: 6 }, (_, i) => v[i] ?? '')
      setDigits(arr)
      notifyChange(v)
      const focusIdx = arr.findIndex((d) => d === '')
      if (focusIdx >= 0) inputsRef.current[focusIdx]?.focus()
      else inputsRef.current[5]?.focus()
      return
    }
    const next = [...digits]
    next[idx] = v.slice(0, 1)
    setDigits(next)
    notifyChange(next.join(''))
    if (v && idx < 5) inputsRef.current[idx + 1]?.focus()
  }

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus()
    }
    if (e.key === 'ArrowLeft' && idx > 0) {
      e.preventDefault()
      inputsRef.current[idx - 1]?.focus()
    }
    if (e.key === 'ArrowRight' && idx < 5) {
      e.preventDefault()
      inputsRef.current[idx + 1]?.focus()
    }
  }

  return (
    <div className={className}>
      {/* Hidden input bound to RHF */}
      <input
        ref={hiddenRef}
        type="hidden"
        name={name}
        value={combined}
        {...props}
      />
      <div className="flex gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <input
            key={i}
            ref={(el) => (inputsRef.current[i] = el)}
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            className="w-10 h-10 border rounded text-center text-lg"
            value={digits[i]}
            onChange={(e) => handleBoxChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
          />)
        )}
      </div>
    </div>
  )
})
